import { useCallback, useEffect, useRef, useState } from 'react';

// Listens for the "sw-update-available" event dispatched from index.js when
// serviceWorkerRegistration's onUpdate fires, and lets the user opt in to
// reloading onto the new version instead of it taking over silently.
const UpdateBanner = () => {
  const [registration, setRegistration] = useState(null);
  // Guards the controllerchange reload below so it only fires after the user
  // explicitly clicks Reload. Without this, the very first-ever SW install
  // also fires "controllerchange" (self.clients.claim() takes control of an
  // uncontrolled page), which would silently reload every first-time visitor.
  const reloadRequestedRef = useRef(false);

  useEffect(() => {
    const handleUpdate = (event) => setRegistration(event.detail);
    window.addEventListener('sw-update-available', handleUpdate);
    return () => window.removeEventListener('sw-update-available', handleUpdate);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const handleControllerChange = () => {
      if (!reloadRequestedRef.current) return;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
  }, []);

  const handleReload = useCallback(() => {
    reloadRequestedRef.current = true;
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      window.location.reload();
    }
  }, [registration]);

  if (!registration) return null;

  return (
    <div
      role="status"
      // bottom-24 (not bottom-4) so this never stacks directly on top of
      // InstallPrompt, which also anchors to the bottom edge and can be
      // visible at the same time as a pending update.
      className="fixed bottom-24 inset-x-4 sm:inset-x-auto sm:right-4 sm:left-auto sm:max-w-sm z-[120] rounded-xl border-2 border-primary/20 bg-white dark:bg-slate-800 shadow-xl px-4 py-3 flex items-center gap-3"
    >
      <i className="fas fa-rotate text-primary" aria-hidden="true"></i>
      <p className="flex-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
        A new version of BizTracker is available.
      </p>
      <button
        onClick={handleReload}
        className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary-dark transition-colors min-h-[36px]"
      >
        Reload
      </button>
    </div>
  );
};

export default UpdateBanner;
